import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Download, Scale, TrendingUp, TrendingDown, Wallet, Info, ArrowDownLeft, ArrowUpRight,
  Plus, Save, Trash2, Pencil, ShoppingCart,
} from 'lucide-react'
import { PageHead } from '../AdminLayout'
import {
  Panel, DataTable, Pager, Toolbar, Select, RefreshButton, Stat, Tag, ConfirmButton,
} from '../ui'
import JalaliInput from '../JalaliInput'
import { CoinIcon } from '../../components/Market'
import { get, post, patch, del, qs } from '../../lib/api'
import { toast } from '../../store'
import { fa, faDate, num, compact, asArray, toEnDigits } from '../../lib/format'
import { toLocalIso } from '../../lib/jalali'

const RANGES: [string, string][] = [
  ['0', 'از ابتدا'], ['7', '۷ روز اخیر'], ['30', '۳۰ روز اخیر'],
  ['90', '۹۰ روز اخیر'], ['365', 'یک سال اخیر'],
]

const money = (v: number) => `${num(Math.round(v || 0))}`
const signed = (v: number) => (v > 0 ? 'text-up' : v < 0 ? 'text-down' : 'text-slate-400')

// صرافی‌های پرکاربرد ایرانی و جهانی — فقط پیشنهاد، مدیر می‌تواند هر نامی بنویسد
const SOURCES = ['نوبیتکس', 'والکس', 'رمزینکس', 'بیت‌پین', 'اکسیر', 'تبدیل',
                 'اوکی‌اکس', 'بایننس', 'بای‌بیت', 'کوکوین', 'حضوری / OTC']

const EMPTY_FORM = {
  id: 0, currency: 'USDT', amount: '', unit_price_irt: '', fee_irt: '',
  source: '', reference: '', note: '', purchased_at: '',
}

/* --------------------------------------------------- ثبت خرید دستی */
function PurchaseForm({ initial, onDone, onCancel }: {
  initial: any; onDone: () => void; onCancel: () => void
}) {
  const [f, setF] = useState<any>(initial)
  const [saving, setSaving] = useState(false)
  useEffect(() => setF(initial), [initial])

  const set = (v: any) => setF((c: any) => ({ ...c, ...v }))

  const amount = Number(toEnDigits(String(f.amount || '')).replace(/,/g, '')) || 0
  const unit = Number(toEnDigits(String(f.unit_price_irt || '')).replace(/,/g, '')) || 0
  const fee = Number(toEnDigits(String(f.fee_irt || '')).replace(/,/g, '')) || 0
  const total = amount * unit
  const effective = amount ? (total + fee) / amount : 0
  const valid = amount > 0 && unit > 0

  const save = async () => {
    setSaving(true)
    try {
      const body = {
        currency: f.currency, amount, unit_price_irt: unit, fee_irt: fee,
        source: f.source, reference: f.reference, note: f.note,
        purchased_at: f.purchased_at,
      }
      if (f.id) await patch(`/admin/accounting/purchases/${f.id}`, body)
      else await post('/admin/accounting/purchases', body)
      toast.ok(f.id ? 'خرید ویرایش شد' : 'خرید ثبت شد')
      onDone()
    } catch (e: any) {
      toast.err(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-2xl border border-brand-500/25 bg-brand-500/[.04] p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
        <Plus className="h-4 w-4 text-brand-300" />
        {f.id ? 'ویرایش خرید' : 'ثبت خرید تازه از صرافی دیگر'}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="label">ارز</label>
          <input className="input num" dir="ltr" value={f.currency}
                 onChange={(e) => set({ currency: e.target.value.toUpperCase() })} />
        </div>
        <div>
          <label className="label">مقدار</label>
          <input className="input num" dir="ltr" inputMode="decimal" placeholder="۱۰۰۰"
                 value={f.amount} onChange={(e) => set({ amount: e.target.value })} />
        </div>
        <div>
          <label className="label">قیمت هر واحد (تومان)</label>
          <input className="input num" dir="ltr" inputMode="decimal" placeholder="۱۰۰۰۰۰"
                 value={f.unit_price_irt}
                 onChange={(e) => set({ unit_price_irt: e.target.value })} />
        </div>
        <div>
          <label className="label">کارمزد خرید (تومان)</label>
          <input className="input num" dir="ltr" inputMode="decimal" placeholder="۰"
                 value={f.fee_irt} onChange={(e) => set({ fee_irt: e.target.value })} />
        </div>

        <div className="sm:col-span-2">
          <label className="label">صرافی / منبع خرید</label>
          <input className="input" list="acc-sources" placeholder="مثلاً نوبیتکس"
                 value={f.source} onChange={(e) => set({ source: e.target.value })} />
          <datalist id="acc-sources">
            {SOURCES.map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>

        <div className="sm:col-span-2">
          <label className="label">تاریخ خرید (شمسی)</label>
          <JalaliInput value={f.purchased_at} onChange={(v) => set({ purchased_at: v })} />
        </div>

        <div className="sm:col-span-2">
          <label className="label">شناسه سفارش / رسید (اختیاری)</label>
          <input className="input num" dir="ltr" value={f.reference}
                 onChange={(e) => set({ reference: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <label className="label">توضیح (اختیاری)</label>
          <input className="input" value={f.note}
                 onChange={(e) => set({ note: e.target.value })} />
        </div>
      </div>

      {/* محاسبه زنده — پیش از ثبت باید معلوم باشد چه عددی وارد دفتر می‌شود */}
      <div className="mt-3 grid gap-3 rounded-xl border border-white/[.07] bg-white/[.02] p-3 sm:grid-cols-3">
        <div>
          <div className="text-[11px] text-slate-500">مبلغ خرید</div>
          <div className="num mt-0.5 font-bold text-white">{num(Math.round(total))} تومان</div>
        </div>
        <div>
          <div className="text-[11px] text-slate-500">مبلغ کل با کارمزد</div>
          <div className="num mt-0.5 font-bold text-white">{num(Math.round(total + fee))} تومان</div>
        </div>
        <div>
          <div className="text-[11px] text-brand-300">بهای تمام‌شده هر واحد</div>
          <div className="num mt-0.5 font-black text-brand-200">{num(Math.round(effective))} تومان</div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={save} disabled={!valid || saving} className="btn-primary !py-2 text-xs">
          <Save className="h-3.5 w-3.5" /> {saving ? 'در حال ثبت…' : f.id ? 'ذخیره تغییرات' : 'ثبت خرید'}
        </button>
        <button onClick={onCancel} className="btn-ghost !py-2 text-xs">انصراف</button>
        {!valid && (
          <span className="self-center text-[11px] text-slate-500">
            مقدار و قیمت هر واحد الزامی است
          </span>
        )}
      </div>
    </div>
  )
}

/* --------------------------------------------------- بخش خریدهای دستی */
function ManualPurchases({ days, onChanged }: { days: string; onChanged: () => void }) {
  const [data, setData] = useState<any>(null)
  const [form, setForm] = useState<any>(null)
  const [page, setPage] = useState(1)
  const [currency, setCurrency] = useState('')
  const [source, setSource] = useState('')
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/accounting/purchases${qs({ days, currency, source, page, per_page: 15 })}`)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }
  useEffect(load, [days, currency, source, page])
  useEffect(() => setPage(1), [days, currency, source])

  const done = () => { setForm(null); load(); onChanged() }
  const rows = asArray<any>(data, 'data')
  const totals = data?.totals || {}

  return (
    <Panel dense className="mb-4"
      title={<span className="flex items-center gap-2">
        <ShoppingCart className="h-4 w-4 text-brand-300" /> خرید دستی از صرافی‌های دیگر
      </span>}
      action={
        !form && (
          <button className="btn-primary !py-2 text-xs"
                  onClick={() => setForm({ ...EMPTY_FORM, purchased_at: toLocalIso(new Date()) })}>
            <Plus className="h-3.5 w-3.5" /> ثبت خرید
          </button>
        )
      }>
      {form && (
        <div className="p-4 pb-0">
          <PurchaseForm initial={form} onDone={done} onCancel={() => setForm(null)} />
        </div>
      )}

      <div className="p-4 pb-0">
        <Toolbar>
          <Select value={currency} onChange={setCurrency}
            options={[['', 'همه ارزها'], ...asArray<string>(data?.currencies).map((c) => [c, c] as [string, string])]} />
          <Select value={source} onChange={setSource}
            options={[['', 'همه صرافی‌ها'], ...asArray<string>(data?.sources).map((s) => [s, s] as [string, string])]} />
        </Toolbar>
      </div>

      <div className="mx-4 mb-3 grid gap-3 rounded-xl border border-white/[.07] bg-white/[.02] p-3 sm:grid-cols-4">
        <div>
          <div className="text-[11px] text-slate-500">تعداد خرید</div>
          <div className="num mt-0.5 font-bold text-white">{fa(totals.count || 0)}</div>
        </div>
        <div>
          <div className="text-[11px] text-slate-500">مقدار کل</div>
          <div className="num mt-0.5 font-bold text-white">{num(totals.qty || 0, 6)}</div>
        </div>
        <div>
          <div className="text-[11px] text-slate-500">هزینه کل (تومان)</div>
          <div className="num mt-0.5 font-bold text-white">{num(totals.irt || 0)}</div>
        </div>
        <div>
          <div className="text-[11px] text-brand-300">میانگین قیمت خرید</div>
          <div className="num mt-0.5 font-black text-brand-200">{num(totals.avgPrice || 0)}</div>
        </div>
      </div>

      <DataTable
        rows={rows} loading={loading} minWidth={900}
        empty="هنوز خرید دستی ثبت نشده است"
        columns={[
          {
            key: 'd', header: 'تاریخ خرید',
            cell: (r: any) => (
              <div>
                <div className="text-slate-200">{faDate(r.purchasedAt, false)}</div>
                <div className="text-[11px] text-slate-600">{faDate(r.purchasedAt).split('—')[1]}</div>
              </div>
            ),
          },
          {
            key: 'c', header: 'ارز',
            cell: (r: any) => (
              <div className="flex items-center gap-2">
                <CoinIcon symbol={r.currency} color={r.color} size={22} />
                <span className="text-slate-100">{r.faName}</span>
              </div>
            ),
          },
          { key: 'a', header: 'مقدار', cell: (r: any) => <span className="num text-slate-200">{num(r.amount, 6)}</span> },
          {
            key: 'p', header: 'قیمت خرید (تومان)',
            cell: (r: any) => <span className="num font-bold text-white">{num(r.unitPrice)}</span>,
          },
          {
            key: 'e', header: 'با کارمزد',
            cell: (r: any) => (
              r.feeIrt
                ? <span className="num text-gold">{num(r.effectiveUnit)}</span>
                : <span className="text-slate-600">—</span>
            ),
          },
          {
            key: 's', header: 'صرافی',
            cell: (r: any) => <Tag kind="info">{r.source || '—'}</Tag>,
          },
          {
            key: 't', header: 'مبلغ کل',
            cell: (r: any) => <span className="num text-slate-300">{num(Math.round(r.totalIrt + r.feeIrt))}</span>,
          },
          {
            key: 'x', header: '',
            cell: (r: any) => (
              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                <button className="btn-ghost !px-3 !py-1.5 text-xs" title="ویرایش"
                  onClick={() => setForm({
                    id: r.id, currency: r.currency, amount: String(r.amount),
                    unit_price_irt: String(r.unitPrice), fee_irt: String(r.feeIrt || ''),
                    source: r.source, reference: r.reference, note: r.note,
                    purchased_at: String(r.purchasedAt).slice(0, 16),
                  })}>
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <ConfirmButton
                  variant="ghost" label="حذف" question="حذف شود؟"
                  icon={<Trash2 className="h-3.5 w-3.5" />}
                  onConfirm={async () => {
                    try {
                      await del(`/admin/accounting/purchases/${r.id}`)
                      toast.ok('حذف شد')
                      done()
                    } catch (e: any) { toast.err(e.message) }
                  }} />
              </div>
            ),
          },
        ]}
      />
      <Pager meta={data?.meta} onPage={setPage} />
    </Panel>
  )
}

export default function Accounting() {
  const [days, setDays] = useState('0')
  const [currency, setCurrency] = useState('')
  const [direction, setDirection] = useState('buy')
  const [page, setPage] = useState(1)

  const [sum, setSum] = useState<any>(null)
  const [pos, setPos] = useState<any>(null)
  const [ledger, setLedger] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    Promise.all([
      get(`/admin/accounting/summary${qs({ days })}`).then(setSum).catch(() => setSum(null)),
      get(`/admin/accounting/positions${qs({ days })}`).then(setPos).catch(() => setPos(null)),
    ]).finally(() => setLoading(false))
  }
  useEffect(load, [days])

  useEffect(() => {
    get(`/admin/accounting/ledger${qs({ days, currency, direction, page, per_page: 25 })}`)
      .then(setLedger)
      .catch(() => setLedger(null))
  }, [days, currency, direction, page])

  useEffect(() => setPage(1), [days, currency, direction])

  const rows = asArray<any>(pos, 'data')
  const led = asArray<any>(ledger, 'data')
  const totals = ledger?.totals || {}
  const estimated = (sum?.estimatedRows || 0) > 0

  const exportUrl = `/api/admin/accounting/export${qs({ days, currency, direction })}`

  return (
    <>
      <PageHead
        title="حسابداری رمزارز"
        subtitle="میانگین بهای تمام‌شده، سود و زیان، و دفتر خرید و فروش"
        action={
          <div className="flex gap-2">
            <Select value={days} onChange={setDays} options={RANGES} />
            <RefreshButton onClick={load} loading={loading} />
          </div>
        }
      />

      {/* هشدار داده تخمینی — عدد تخمینی نباید قطعی به‌نظر برسد */}
      {estimated && (
        <div className="card mb-4 flex items-start gap-3 border-gold/30 bg-gold/[.06] p-4">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          <p className="text-xs leading-6 text-slate-200">
            {fa(sum.estimatedRows)} معامله ارزش ریالیِ ثبت‌شده ندارند (مربوط به پیش از
            راه‌اندازی این بخش، روی جفت‌های غیرریالی). ارزش آن‌ها با <b>نرخ امروز</b> حساب
            شده و در ستون‌ها با نشان «تخمینی» مشخص است. معامله‌های تازه دقیق ثبت می‌شوند.
          </p>
        </div>
      )}

      {/* ── کارت‌های خلاصه */}
      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="ارزش موجودی به نرخ روز" color="#7C5CFF" icon={<Wallet className="h-5 w-5" />}
          value={money(sum?.positionValue || 0)}
          hint={`بهای تمام‌شده: ${compact(sum?.costValue || 0)} تومان`} />
        <Stat label="خرید کل (از کاربران)" color="#00E39A" icon={<ArrowDownLeft className="h-5 w-5" />}
          value={money(sum?.boughtIrt || 0)}
          hint={`فروش کل: ${compact(sum?.soldIrt || 0)} تومان`} />
        <Stat label="سود محقق‌شده" color={(sum?.realized || 0) >= 0 ? '#00E39A' : '#FF4D6D'}
          icon={<TrendingUp className="h-5 w-5" />}
          value={money(sum?.realized || 0)}
          hint="از فروش‌های انجام‌شده" />
        <Stat label="سود محقق‌نشده" color={(sum?.unrealized || 0) >= 0 ? '#22D3EE' : '#FF4D6D'}
          icon={<TrendingDown className="h-5 w-5" />}
          value={money(sum?.unrealized || 0)}
          hint="روی موجودی فعلی، با نرخ روز" />
      </div>

      {/* ── خریدهای دستی */}
      <ManualPurchases days={days} onChanged={load} />

      {/* ── میانگین بهای تمام‌شده هر ارز */}
      <Panel dense className="mb-4"
        title={<span className="flex items-center gap-2"><Scale className="h-4 w-4 text-brand-300" /> میانگین قیمت خرید هر ارز</span>}>
        <DataTable
          rows={rows} loading={loading} minWidth={980}
          empty="هنوز معامله‌ای ثبت نشده است"
          columns={[
            {
              key: 'c', header: 'ارز',
              cell: (r: any) => (
                <div className="flex items-center gap-2">
                  <CoinIcon symbol={r.currency} color={r.color} size={26} />
                  <div className="min-w-0">
                    <div className="truncate text-slate-100">{r.faName}</div>
                    <div className="num text-[11px] text-slate-500" dir="ltr">{r.currency}</div>
                  </div>
                </div>
              ),
            },
            {
              key: 'avg', header: 'میانگین خرید ما',
              cell: (r: any) => (
                <span className="num font-bold text-white">{num(r.avgBuyPrice)}</span>
              ),
            },
            {
              key: 'src', header: 'به تفکیک منبع',
              cell: (r: any) => (
                <div className="text-[11px] leading-5">
                  <div className="text-slate-400">
                    از کاربران: <span className="num text-slate-200">
                      {r.fromUsersQty ? num(r.fromUsersAvg) : '—'}
                    </span>
                  </div>
                  <div className="text-slate-400">
                    خرید دستی: <span className="num text-slate-200">
                      {r.manualQty ? num(r.manualAvg) : '—'}
                    </span>
                  </div>
                </div>
              ),
            },
            {
              key: 'sell', header: 'میانگین فروش ما',
              cell: (r: any) => (
                <span className="num text-slate-300">{r.avgSellPrice ? num(r.avgSellPrice) : '—'}</span>
              ),
            },
            {
              key: 'sp', header: 'حاشیه',
              cell: (r: any) => (
                r.avgSellPrice
                  ? <span className={`num text-xs font-semibold ${signed(r.spread)}`}>
                      {r.marginPercent > 0 ? '+' : ''}{fa(r.marginPercent)}٪
                    </span>
                  : <span className="text-slate-600">—</span>
              ),
            },
            {
              key: 'mk', header: 'نرخ روز',
              cell: (r: any) => <span className="num text-slate-300">{num(r.marketPrice)}</span>,
            },
            {
              key: 'q', header: 'موجودی',
              cell: (r: any) => (
                <div>
                  <div className="num text-slate-100">{num(r.netQty, 6)}</div>
                  <div className="num text-[11px] text-slate-500">{compact(r.positionValue)} ت</div>
                </div>
              ),
            },
            {
              key: 'r', header: 'سود محقق‌شده',
              cell: (r: any) => <span className={`num ${signed(r.realized)}`}>{compact(r.realized)}</span>,
            },
            {
              key: 'u', header: 'سود محقق‌نشده',
              cell: (r: any) => <span className={`num ${signed(r.unrealized)}`}>{compact(r.unrealized)}</span>,
            },
            {
              key: 'n', header: 'معامله',
              cell: (r: any) => (
                <span className="num text-slate-500">
                  {fa(r.trades)}
                  {r.estimatedRows > 0 && <span className="mr-1 text-gold" title="شامل ردیف تخمینی">*</span>}
                </span>
              ),
            },
          ]}
        />
      </Panel>

      {/* ── دفتر معاملات */}
      <Panel dense
        title={
          <span className="flex items-center gap-2">
            {direction === 'buy'
              ? <><ArrowDownLeft className="h-4 w-4 text-up" /> خریدهای ما (فروش کاربران به صرافی)</>
              : <><ArrowUpRight className="h-4 w-4 text-down" /> فروش‌های ما (خرید کاربران از صرافی)</>}
          </span>
        }
        action={
          <a href={exportUrl} className="btn-ghost !py-2 text-xs">
            <Download className="h-3.5 w-3.5" /> خروجی اکسل
          </a>
        }
      >
        <div className="p-4 pb-0">
          <Toolbar>
            <Select value={direction} onChange={setDirection}
              options={[['buy', 'خرید ما (کاربر فروخته)'], ['sell', 'فروش ما (کاربر خریده)'], ['', 'همه']]} />
            <Select value={currency} onChange={setCurrency}
              options={[['', 'همه ارزها'],
                        ...asArray<any>(pos, 'data').map((r: any) => [r.currency, r.faName] as [string, string])]} />
          </Toolbar>
        </div>

        {/* جمع همین فیلتر — نه جمع کل، تا عددی که دیده می‌شود با فیلتر بخواند */}
        <div className="mx-4 mb-3 grid gap-3 rounded-xl border border-white/[.07] bg-white/[.02] p-3 sm:grid-cols-4">
          <div>
            <div className="text-[11px] text-slate-500">تعداد معامله</div>
            <div className="num mt-0.5 font-bold text-white">{fa(totals.count || 0)}</div>
          </div>
          <div>
            <div className="text-[11px] text-slate-500">مقدار کل</div>
            <div className="num mt-0.5 font-bold text-white">{num(totals.qty || 0, 6)}</div>
          </div>
          <div>
            <div className="text-[11px] text-slate-500">ارزش کل (تومان)</div>
            <div className="num mt-0.5 font-bold text-white">{num(totals.irt || 0)}</div>
          </div>
          <div>
            <div className="text-[11px] text-brand-300">
              میانگین قیمت {direction === 'buy' ? 'خرید' : 'فروش'} ما
            </div>
            <div className="num mt-0.5 font-black text-brand-200">{num(totals.avgPrice || 0)}</div>
          </div>
        </div>

        <DataTable
          rows={led} minWidth={900} empty="معامله‌ای در این محدوده نیست"
          columns={[
            {
              key: 'd', header: 'تاریخ فروش',
              cell: (r: any) => <span className="text-slate-300">{faDate(r.at)}</span>,
            },
            {
              key: 'c', header: 'نوع ارز',
              cell: (r: any) => (
                <div className="flex items-center gap-2">
                  <CoinIcon symbol={r.currency} color={r.color} size={22} />
                  <span className="text-slate-100">{r.faName}</span>
                </div>
              ),
            },
            {
              key: 'a', header: 'مقدار',
              cell: (r: any) => <span className="num text-slate-200">{num(r.qty, 6)}</span>,
            },
            {
              key: 'p', header: 'قیمت واحد (تومان)',
              cell: (r: any) => (
                <span className="num font-bold text-white">
                  {num(Math.round(r.unitIrt))}
                  {r.estimated && <span className="mr-1 text-[10px] text-gold">تخمینی</span>}
                </span>
              ),
            },
            {
              key: 't', header: 'ارزش کل (تومان)',
              cell: (r: any) => <span className="num text-slate-300">{num(Math.round(r.totalIrt))}</span>,
            },
            {
              key: 'k', header: 'نوع',
              cell: (r: any) => (
                <Tag kind={r.kind === 'convert' ? 'info' : 'muted'}>
                  {r.kind === 'convert' ? 'تبدیل سریع' : 'معامله'}
                </Tag>
              ),
            },
            {
              key: 'u', header: 'کاربر',
              cell: (r: any) => (
                <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">
                  {r.email}
                </Link>
              ),
            },
          ]}
        />
        <Pager meta={ledger?.meta} onPage={setPage} />
      </Panel>
    </>
  )
}
